import { useState, useRef, useEffect } from "react";
import { X, Send, Mail, ThumbsUp, ThumbsDown, Volume2, VolumeX, Loader2, Mic, MicOff, Copy, ExternalLink, Calculator, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useChatSound } from "@/hooks/useChatSound";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import ricksLogo from "@/assets/ricks-logo-premium.png";
import aiAvatar from "@/assets/ai-assistant-avatar.png";

interface Message {
  id: number;
  text: string;
  isBot: boolean;
  timestamp: Date;
  seen?: boolean;
  rating?: number; // -1, 0, or 1
}

interface AIChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  onInteraction: () => void;
}

// Voice status type for UI display
type VoiceStatus = 'idle' | 'listening' | 'thinking' | 'speaking';

export const AIChatWidget = ({ isOpen, onClose, onInteraction }: AIChatWidgetProps) => {
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSendingTranscript, setIsSendingTranscript] = useState(false);
  const [userTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
    } catch (e) {
      console.error("Error detecting timezone:", e);
      return "America/Chicago";
    }
  });
  const [transcriptSent, setTranscriptSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const closeStateRef = useRef<{ messages: Message[]; transcriptSent: boolean; conversationId: string | null }>({
    messages: [],
    transcriptSent: false,
    conversationId: null,
  });
  const { toast } = useToast();
  const { playSound } = useChatSound();
  
  // Voice state - default ON for first-time visitors
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const stored = localStorage.getItem('voiceEnabled');
    return stored !== null ? stored === 'true' : true; // Default ON
  });
  const [currentSpeakingMessageId, setCurrentSpeakingMessageId] = useState<number | null>(null);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>('idle');
  
  // Save voice preference
  useEffect(() => {
    localStorage.setItem('voiceEnabled', String(voiceEnabled));
  }, [voiceEnabled]);
  
  // Refs to avoid circular dependency between STT and TTS hooks
  const isSpeakingRef = useRef(false);
  const stopSpeakingRef = useRef<(() => void) | null>(null);
  const pauseForTTSRef = useRef<(() => void) | null>(null);
  const resumeAfterTTSRef = useRef<(() => void) | null>(null);

  // Reference to track if we should send the message from voice
  const pendingVoiceMessageRef = useRef<string | null>(null);

  // Speech-to-text hook with turn-taking support
  const { 
    isListening, 
    isSupported: isSpeechSupported, 
    startListening, 
    stopListening,
    abortListening,
    pauseForTTS,
    resumeAfterTTS,
    isPaused: isMicPaused
  } = useSpeechToText({
    onResult: (transcript) => {
      if (transcript.trim()) {
        // Stop any current TTS playback when user speaks
        if (isSpeakingRef.current && stopSpeakingRef.current) {
          stopSpeakingRef.current();
          setCurrentSpeakingMessageId(null);
        }
        // Set the input and trigger send
        pendingVoiceMessageRef.current = transcript;
        setInputValue(transcript);
      }
    },
    onError: (errorMessage) => {
      setVoiceStatus('idle');
      toast({
        title: "Voice Input Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
    onStart: () => {
      setVoiceStatus('listening');
      // Stop any current TTS when user starts speaking
      if (isSpeakingRef.current && stopSpeakingRef.current) {
        stopSpeakingRef.current();
        setCurrentSpeakingMessageId(null);
      }
    },
    onEnd: () => {
      if (!isTyping) {
        setVoiceStatus('idle');
      }
    },
  });

  // Store pauseForTTS and resumeAfterTTS in refs for TTS hook to use
  useEffect(() => {
    pauseForTTSRef.current = pauseForTTS;
    resumeAfterTTSRef.current = resumeAfterTTS;
  }, [pauseForTTS, resumeAfterTTS]);

  // Text-to-speech with turn-taking integration
  const { speak, stop: stopSpeaking, isSpeaking, isLoading: isVoiceLoading } = useTextToSpeech({
    onStart: () => {
      setVoiceStatus('speaking');
      // Pause mic while speaking
      if (pauseForTTSRef.current) {
        pauseForTTSRef.current();
      }
    },
    onEnd: () => {
      setCurrentSpeakingMessageId(null);
      setVoiceStatus('idle');
      // Resume mic after speaking (if voice mode is on)
      if (voiceEnabled && resumeAfterTTSRef.current) {
        console.log('[TTS] Ended, resuming mic for turn-taking');
        resumeAfterTTSRef.current();
      }
    },
    onError: (error) => {
      console.error('TTS error:', error);
      setCurrentSpeakingMessageId(null);
      setVoiceStatus('idle');
      // Resume mic even on error
      if (voiceEnabled && resumeAfterTTSRef.current) {
        resumeAfterTTSRef.current();
      }
      toast({
        title: "Voice Error",
        description: "Unable to play voice. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Keep refs in sync with current values
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
    stopSpeakingRef.current = stopSpeaking;
  }, [isSpeaking, stopSpeaking]);
  
  // Auto-send voice message when transcript is received
  useEffect(() => {
    if (pendingVoiceMessageRef.current && inputValue === pendingVoiceMessageRef.current) {
      pendingVoiceMessageRef.current = null;
      handleSend();
    }
  }, [inputValue]);

  // Track if this is a first-time visitor for voice greeting
  const [isFirstVisit] = useState(() => {
    const hasVisited = localStorage.getItem('hasVisitedChat');
    if (!hasVisited) {
      localStorage.setItem('hasVisitedChat', 'true');
      return true;
    }
    return false;
  });

  // Show typing animation then welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setIsTyping(true);
      
      const welcomeTimer = setTimeout(() => {
        setIsTyping(false);
        
        const now = new Date();
        const userLocalTime = new Date(now.toLocaleString("en-US", { timeZone: userTimezone }));
        const hour = userLocalTime.getHours();
        
        let greeting = "Good evening";
        if (hour >= 4 && hour < 12) {
          greeting = "Good morning";
        } else if (hour >= 12 && hour < 18) {
          greeting = "Good afternoon";
        }
        
        const welcomeText = `${greeting}! I'm Denise. Where can I take you today?`;
        
        setMessages([
          {
            id: 1,
            text: welcomeText,
            isBot: true,
            timestamp: new Date(),
          },
        ]);
        playSound('message');
        
        // Auto-play voice greeting for first-time visitors with voice enabled
        if (isFirstVisit && voiceEnabled) {
          setTimeout(() => {
            setCurrentSpeakingMessageId(1);
            setVoiceStatus('speaking');
            speak(welcomeText);
          }, 500); // Small delay after message appears
        }
      }, 1500);

      return () => clearTimeout(welcomeTimer);
    }
  }, [isOpen, userTimezone, isFirstVisit, voiceEnabled, speak]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Validate URL scheme to prevent XSS attacks via javascript: or data: URLs
  const isSafeUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url, window.location.href);
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  // Format message text to render markdown links as clickable HTML links
  const formatMessageWithLinks = (text: string): string => {
    // Convert markdown links [text](url) to HTML anchor tags with URL validation
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let formattedText = text.replace(markdownLinkRegex, (_, linkText, url) => {
      // Validate URL scheme to prevent XSS
      if (!isSafeUrl(url)) {
        return linkText; // Strip unsafe links, keep text only
      }
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary font-semibold underline hover:text-primary/80 transition-colors">${linkText}</a>`;
    });
    
    // Remove phone numbers - we'll show them as buttons instead
    formattedText = formattedText.replace(
      /\*?\*?\(210\) 296-7409\*?\*?/g,
      ''
    );
    formattedText = formattedText.replace(
      /call\s*(?:us\s*)?(?:at\s*)?(?:directly\s*)?(?:at\s*)?\(210\)\s*296-7409/gi,
      ''
    );
    formattedText = formattedText.replace(
      /(?:call|phone|contact).*?\(210\)\s*296-7409/gi,
      ''
    );
    
    // Remove email addresses - we'll show them as buttons instead  
    formattedText = formattedText.replace(
      /ricksblackcarservice@outlook\.com/gi,
      ''
    );
    
    // Clean up leftover "or" connectors and extra spaces
    formattedText = formattedText.replace(/\s+or\s+\./g, '.');
    formattedText = formattedText.replace(/,\s*or\s*\./g, '.');
    formattedText = formattedText.replace(/\s{2,}/g, ' ');
    
    // Convert **bold** to <strong>
    formattedText = formattedText.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    return formattedText;
  };

  // Check if message contains contact info (phone or should show contact buttons)
  const shouldShowContactButtons = (text: string): boolean => {
    return text.includes('(210) 296-7409') || 
           text.includes('ricksblackcarservice@outlook.com') ||
           text.toLowerCase().includes('call us') ||
           text.toLowerCase().includes('contact us') ||
           text.includes('TOTAL FARE') ||
           // Formula-based pricing detection (any dollar amount in quote)
           /\$\d+\.\d{2}/.test(text) ||
           text.includes('$474') ||
           text.includes('TOTAL');
  };
  const handleSend = async () => {
    if (!inputValue.trim()) return;

    onInteraction();

    const goodbyeWords = ["goodbye", "bye", "thanks bye", "thank you bye", "see you", "talk later", "gotta go"];
    const isGoodbye = goodbyeWords.some(word => inputValue.toLowerCase().includes(word));

    const userMessage: Message = {
      id: Date.now(),
      text: inputValue,
      isBot: false,
      timestamp: new Date(),
      seen: false,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);
    setVoiceStatus('thinking');

    // Mark message as seen after a brief delay
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id ? { ...msg, seen: true } : msg
        )
      );
    }, 1000);

    try {
      const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;
      
      const conversationHistory = messages.map((msg) => ({
        role: msg.isBot ? "assistant" : "user",
        content: msg.text,
      }));

      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [...conversationHistory, { role: "user", content: userMessage.text }],
          sessionId,
          timezone: userTimezone,
          conversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Capture conversation ID from response headers
      const convId = response.headers.get("X-Conversation-Id");
      if (convId && !conversationId) {
        setConversationId(convId);
        // Store in sessionStorage for conversion tracking
        sessionStorage.setItem("chatConversationId", convId);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let assistantMessageId: number | null = null;
      let textBuffer = ""; // Buffer for partial lines

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        // Process complete lines only
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          // Handle CRLF
          if (line.endsWith("\r")) line = line.slice(0, -1);
          
          // Skip empty lines and comments
          if (!line.trim() || line.startsWith(":")) continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            
            if (content) {
              assistantText += content;
              
              if (assistantMessageId === null) {
                assistantMessageId = Date.now();
                setMessages((prev) => [
                  ...prev,
                  {
                    id: assistantMessageId!,
                    text: assistantText,
                    isBot: true,
                    timestamp: new Date(),
                  },
                ]);
                // Play gentle notification sound when AI responds
                playSound('message');
              } else {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, text: assistantText }
                      : msg
                  )
                );
              }
            }
          } catch {
            // Incomplete JSON, put line back in buffer and wait for more data
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Process any remaining buffer
      if (textBuffer.trim()) {
        const lines = textBuffer.split("\n");
        for (const line of lines) {
          if (!line.trim() || line.startsWith(":") || !line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantText += content;
              if (assistantMessageId !== null) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, text: assistantText }
                      : msg
                  )
                );
              }
            }
          } catch {
            // Ignore incomplete data at end
          }
        }
      }

      setIsTyping(false);
      setVoiceStatus('idle');
      
      // Auto-speak the AI response if voice is enabled
      if (voiceEnabled && assistantText.trim() && assistantMessageId) {
        setCurrentSpeakingMessageId(assistantMessageId);
        setVoiceStatus('speaking');
        speak(assistantText);
      }
      
      // If user said goodbye, auto-send transcript
      if (isGoodbye) {
        setTimeout(() => {
          handleAutoSendTranscript("user said goodbye");
        }, 2000); // Wait 2 seconds after AI responds
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsTyping(false);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Extract contact info from messages
  const extractContactInfo = () => {
    const allText = messages.map(m => m.text).join(' ');
    
    // Email regex
    const emailMatch = allText.match(/[\w.-]+@[\w.-]+\.\w+/i);
    const email = emailMatch ? emailMatch[0] : null;
    
    // Phone regex (various formats)
    const phoneMatch = allText.match(/(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/);
    const phone = phoneMatch ? phoneMatch[0] : null;
    
    // Name extraction
    let full_name: string | null = null;
    for (const msg of messages) {
      if (!msg.isBot) {
        const content = msg.text;
        const namePattern1 = content.match(/(?:my name is|i am|i'm|this is|name's)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
        if (namePattern1) {
          full_name = namePattern1[1];
          break;
        }
        const namePattern2 = content.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)(?:\s*[,\-]|\s+\d|\s+[\w.]+@)/i);
        if (namePattern2) {
          full_name = namePattern2[1];
          break;
        }
      }
    }
    
    return { full_name, phone, email };
  };

  // Capture lead when chat closes
  const captureLeadOnClose = async () => {
    if (!conversationId || messages.length <= 1) return;

    const contactInfo = extractContactInfo();
    
    // Only capture if we have name and at least one contact method
    if (!contactInfo.full_name || (!contactInfo.phone && !contactInfo.email)) {
      console.log("No contact info detected, skipping lead capture");
      return;
    }

    const fullTranscript = messages
      .map(m => `${m.isBot ? 'Denise' : 'User'}: ${m.text}`)
      .join('\n\n');

    console.log("Capturing lead on chat close:", contactInfo);

    try {
      const response = await supabase.functions.invoke("capture-lead", {
        body: {
          user_data: {
            full_name: contactInfo.full_name,
            phone: contactInfo.phone || '',
            email: contactInfo.email || '',
          },
          full_transcript: fullTranscript,
          conversation_id: conversationId,
        },
      });

      if (response.error) throw response.error;
      console.log("Lead captured successfully on close");
    } catch (error) {
      console.error("Error capturing lead:", error);
    }
  };

  const handleAutoSendTranscript = async (reason: string) => {
    // ONLY send ONE email when chat closes - prevent duplicates
    if (!conversationId || messages.length <= 1 || transcriptSent) {
      return;
    }

    console.log(`Sending single transcript on chat close due to: ${reason}`);
    setTranscriptSent(true); // Set immediately to prevent duplicate calls

    try {
      // Mark conversation as ended
      await supabase
        .from("chat_conversations")
        .update({ ended_at: new Date().toISOString() })
        .eq("id", conversationId);

      // Build full transcript from frontend messages (includes AI responses)
      const fullTranscript = messages.map(m => ({
        role: m.isBot ? 'assistant' : 'user',
        content: m.text,
        timestamp: m.timestamp.toISOString()
      }));

      // Send email with complete chat history from frontend
      const response = await supabase.functions.invoke("send-chat-transcript", {
        body: { 
          conversationId,
          fullTranscript // Send complete chat history from frontend state
        },
      });

      if (response.error) throw response.error;

      console.log("Complete transcript sent successfully (single email)");
    } catch (error) {
      console.error("Error sending transcript:", error);
      // Don't reset transcriptSent - prevents retry loops that send multiple emails
    }
  };

  // Keep latest close-relevant state in a ref so we can safely act on close events
  useEffect(() => {
    closeStateRef.current = { messages, transcriptSent, conversationId };
  }, [messages, transcriptSent, conversationId]);

  // Ensure transcript + lead capture run whenever the chat becomes closed (not just when X is clicked)
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      const { messages: latestMessages, transcriptSent: latestTranscriptSent } = closeStateRef.current;

      console.log("Chat closed (isOpen -> false). Checking transcript send...");

      if (latestMessages.length > 1 && !latestTranscriptSent) {
        void handleAutoSendTranscript("chat closed (prop change)");
      }

      void captureLeadOnClose();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);


  const handleSendTranscript = async () => {
    if (!conversationId || messages.length <= 1) {
      toast({
        title: "No conversation to send",
        description: "Start a conversation first before sending a transcript.",
      });
      return;
    }

    setIsSendingTranscript(true);

    try {
      const response = await supabase.functions.invoke("send-chat-transcript", {
        body: { conversationId },
      });

      if (response.error) throw response.error;

      // Mark conversation as ended
      await supabase
        .from("chat_conversations")
        .update({ ended_at: new Date().toISOString() })
        .eq("id", conversationId);

      setTranscriptSent(true);

      toast({
        title: "Transcript sent!",
        description: "The conversation transcript has been emailed to Rick.",
      });
    } catch (error) {
      console.error("Error sending transcript:", error);
      toast({
        title: "Error",
        description: "Failed to send transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingTranscript(false);
    }
  };


  const log_feedback = async (question: string, response: string, score: number, messageId: number) => {
    try {
      // Securely insert feedback data into ai_chat_feedback table
      const { error } = await supabase
        .from("ai_chat_feedback")
        .insert({
          timestamp: new Date().toISOString(),
          user_question: question,
          ai_response: response,
          rating: score,
        });

      if (error) throw error;

      // Update message rating in state to hide/disable icons
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, rating: score } : msg
        )
      );

      toast({
        title: score === 1 ? "Thanks for the feedback!" : "Feedback received",
        description: score === 1 
          ? "We're glad Denise could help!" 
          : "We'll use this to improve Denise's responses.",
      });
    } catch (error) {
      console.error("Error logging feedback:", error);
      toast({
        title: "Error",
        description: "Failed to log feedback. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed inset-4 sm:bottom-6 sm:right-6 sm:top-auto sm:left-auto z-[99999] sm:w-96 sm:h-[600px] sm:max-h-[80vh] bg-card border border-border rounded-2xl sm:rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in"
          style={{
            height: '100dvh',
            maxHeight: '100dvh',
            WebkitFillAvailable: 'height'
          } as React.CSSProperties & { WebkitFillAvailable?: string }}
          onTouchStart={(e) => {
            onInteraction();
            e.stopPropagation();
          }}
          onClick={(e) => {
            onInteraction();
            e.stopPropagation();
          }}
        >
          {/* Header - Luxury Black & Blue Theme */}
          <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 text-white px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between border-b border-blue-500/30">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src={aiAvatar} 
                alt="Denise AI Assistant" 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover bg-black/20 p-1 ring-2 ring-blue-500/50"
              />
              <div>
                <h3 className="font-semibold text-sm sm:text-base text-white">Denise AI Assistant</h3>
                <p className="text-xs text-blue-400 hidden sm:block">Your Luxury Travel Concierge</p>
                {/* Voice Status Indicator */}
                <p className={`text-xs flex items-center gap-1 ${
                  voiceStatus === 'listening' ? 'text-red-400' :
                  voiceStatus === 'thinking' ? 'text-blue-300' :
                  voiceStatus === 'speaking' ? 'text-blue-400' :
                  'text-emerald-400'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    voiceStatus === 'listening' ? 'bg-red-400 animate-pulse' :
                    voiceStatus === 'thinking' ? 'bg-blue-300 animate-pulse' :
                    voiceStatus === 'speaking' ? 'bg-blue-400 animate-pulse' :
                    'bg-emerald-400 animate-pulse'
                  }`}></span>
                  {voiceStatus === 'listening' ? 'Listening...' :
                   voiceStatus === 'thinking' ? 'Thinking...' :
                   voiceStatus === 'speaking' ? 'Speaking...' :
                   'Ready'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Voice Toggle Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (isSpeaking) {
                    stopSpeaking();
                    setCurrentSpeakingMessageId(null);
                  }
                  setVoiceEnabled(!voiceEnabled);
                  toast({
                    title: voiceEnabled ? "Voice disabled" : "Voice enabled",
                    description: voiceEnabled 
                      ? "AI responses will no longer be spoken" 
                      : "AI responses will now be spoken aloud",
                  });
                }}
                className={`relative text-white hover:bg-white/10 h-8 w-8 sm:h-9 sm:w-9 ${voiceEnabled ? 'bg-blue-600/30' : ''}`}
                title={voiceEnabled ? "Disable voice" : "Enable voice"}
                aria-label={voiceEnabled ? "Disable voice output" : "Enable voice output"}
              >
                {/* Pulsing ring animation to draw attention */}
                {!voiceEnabled && !isVoiceLoading && (
                  <span className="absolute inset-0 rounded-md animate-ping bg-blue-500/40 pointer-events-none" style={{ animationDuration: '2s' }} />
                )}
                {isVoiceLoading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : voiceEnabled ? (
                  <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSendTranscript}
                disabled={isSendingTranscript || messages.length <= 1 || transcriptSent}
                className="text-white hover:bg-white/10 h-8 w-8 sm:h-9 sm:w-9"
                title="Email transcript to Rick"
                aria-label="Email chat transcript to Rick"
              >
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (isListening) abortListening();
                  if (isSpeaking) stopSpeaking();
                  if (messages.length > 1 && !transcriptSent) {
                    // Send transcript and wait for it to complete before closing
                    await handleAutoSendTranscript("user closed widget");
                  }
                  onClose();
                }}
                className="text-white hover:bg-white/10 h-8 w-8 sm:h-9 sm:w-9"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>

          {/* Messages - Dark elegant background with blue accent */}
          <ScrollArea className="flex-1 p-3 sm:p-4 bg-gradient-to-b from-zinc-950 to-zinc-900">
            <div className="space-y-3 sm:space-y-4">
              {messages.map((message, index) => {
                // Find the previous user question for this AI message
                let userQuestion = "";
                if (message.isBot) {
                  for (let i = index - 1; i >= 0; i--) {
                    if (!messages[i].isBot) {
                      userQuestion = messages[i].text;
                      break;
                    }
                  }
                }

                return (
                  <div
                    key={message.id}
                    className={`flex ${message.isBot ? "justify-start" : "justify-end"} gap-2`}
                  >
                    {message.isBot && (
                      <div className="relative flex-shrink-0 mt-1">
                        <img 
                          src={aiAvatar} 
                          alt="AI Assistant" 
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-2 min-w-0 max-w-[85%] sm:max-w-[80%] overflow-hidden">
                      <div
                        className={`rounded-lg px-3 py-2 sm:px-4 transition-all duration-300 overflow-hidden ${
                          message.isBot
                            ? currentSpeakingMessageId === message.id && isSpeaking
                              ? "bg-gradient-to-br from-zinc-800 to-zinc-900 border-2 border-[rgb(212,175,55)] text-white shadow-sm"
                              : "bg-gradient-to-br from-zinc-800 to-zinc-900 border border-blue-500/20 text-white"
                            : "bg-blue-600 text-white"
                        }`}
                      >
                        <div className="flex items-start gap-2 min-w-0 overflow-hidden">
                          <div 
                            className="text-sm whitespace-pre-wrap flex-1 min-w-0 break-words" 
                            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                            dangerouslySetInnerHTML={{ __html: formatMessageWithLinks(message.text) }} 
                          />
                          {message.isBot && currentSpeakingMessageId === message.id && isSpeaking && (
                            <Volume2 className="w-4 h-4 text-[rgb(212,175,55)] flex-shrink-0 animate-pulse mt-0.5" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 justify-between">
                          <p
                            className={`text-xs mt-1 ${
                              message.isBot ? "text-zinc-400" : "opacity-80"
                            }`}
                          >
                            {message.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                          {!message.isBot && (
                            <p className="text-xs mt-1 opacity-70">
                              {message.seen ? "Seen" : "Sent"}
                            </p>
                          )}
                        </div>
                        
                        {/* Contact Action Buttons - Show for bot messages with contact info or quotes */}
                        {message.isBot && shouldShowContactButtons(message.text) && (
                          <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-zinc-700">
                            {/* Call Button */}
                            <a
                              href="tel:+12102967409"
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-md transition-colors"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              Call Now
                            </a>
                            {/* Email Button */}
                            <a
                              href="mailto:ricksblackcarservice@outlook.com?subject=Transportation%20Inquiry"
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-md transition-colors"
                            >
                              <Mail className="w-3.5 h-3.5" />
                              Email Us
                            </a>
                            {/* Book Now Button */}
                            <a
                              href="https://ricksblackcarservicesantonio.com/book"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Book Online
                            </a>
                            {/* Copy Quote - only for quotes */}
                            {message.text.includes('TOTAL') && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(message.text);
                                  toast({
                                    title: "Quote copied!",
                                    description: "The fare quote has been copied to your clipboard.",
                                  });
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-zinc-700 hover:bg-zinc-600 text-white rounded-md transition-colors"
                              >
                                <Copy className="w-3.5 h-3.5" />
                                Copy Quote
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Voice & Feedback Icons - Only show for bot messages */}
                      {message.isBot && (
                        <div className="flex gap-1">
                          {/* Play/Stop voice button */}
                          <button
                            onClick={() => {
                              if (currentSpeakingMessageId === message.id && isSpeaking) {
                                stopSpeaking();
                                setCurrentSpeakingMessageId(null);
                                setVoiceStatus('idle');
                              } else {
                                if (isSpeaking) stopSpeaking();
                                setCurrentSpeakingMessageId(message.id);
                                setVoiceStatus('speaking');
                                speak(message.text);
                              }
                            }}
                            disabled={isVoiceLoading}
                            className="p-1 rounded-md transition-all hover:scale-110 opacity-70 hover:opacity-100"
                            title={currentSpeakingMessageId === message.id && isSpeaking ? "Stop speaking" : "Listen to response"}
                            aria-label={currentSpeakingMessageId === message.id && isSpeaking ? "Stop speaking" : "Listen to response"}
                          >
                            {isVoiceLoading && currentSpeakingMessageId === message.id ? (
                              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                            ) : currentSpeakingMessageId === message.id && isSpeaking ? (
                              <VolumeX className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Volume2 className="w-4 h-4 text-blue-400" />
                            )}
                          </button>
                          
                          {/* Feedback buttons - only show when not yet rated */}
                          {message.rating === undefined && (
                            <>
                              <button
                                onClick={() => log_feedback(userQuestion, message.text, 1, message.id)}
                                className="p-1 rounded-md transition-all hover:scale-110 opacity-70 hover:opacity-100"
                                title="Helpful response"
                                aria-label="Mark response as helpful"
                              >
                                <ThumbsUp className="w-4 h-4 text-emerald-500" />
                              </button>
                              <button
                                onClick={() => log_feedback(userQuestion, message.text, -1, message.id)}
                                className="p-1 rounded-md transition-all hover:scale-110 opacity-70 hover:opacity-100"
                                title="Not helpful"
                                aria-label="Mark response as not helpful"
                              >
                                <ThumbsDown className="w-4 h-4 text-red-500" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {isTyping && (
                <div className="flex justify-start gap-2">
                  <div className="relative flex-shrink-0 mt-1">
                    <img 
                      src={aiAvatar} 
                      alt="AI Assistant" 
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                    />
                    {/* Pulsing ring when calculating */}
                    <div className="absolute inset-0 rounded-full ring-2 ring-blue-500/50 animate-pulse" />
                  </div>
                  <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-blue-500/20 rounded-lg px-3 py-2 sm:px-4 sm:py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce"></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input - Luxury Black & Blue Footer */}
          <div className="p-3 sm:p-4 border-t border-blue-500/20 bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900">
            {/* Quick Quote Prompts - Show only after first message */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={() => {
                    setInputValue("I need a ride from Downtown San Antonio to SAT Airport");
                    onInteraction();
                  }}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 rounded-md transition-colors"
                >
                  <Calculator className="w-3 h-3" />
                  Downtown → Airport
                </button>
                <button
                  onClick={() => {
                    setInputValue("What's the fare from Boerne to SAT Airport?");
                    onInteraction();
                  }}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 rounded-md transition-colors"
                >
                  <Calculator className="w-3 h-3" />
                  Boerne → Airport
                </button>
                <button
                  onClick={() => {
                    setInputValue("I need to go from San Antonio to Austin");
                    onInteraction();
                  }}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30 rounded-md transition-colors"
                >
                  <Calculator className="w-3 h-3" />
                  SA → Austin
                </button>
              </div>
            )}
            
            {/* Voice Status Indicator */}
            {voiceStatus !== 'idle' && (
              <div className="flex items-center justify-center gap-2 mb-2 py-1.5 px-3 bg-zinc-800/80 rounded-full mx-auto w-fit">
                {voiceStatus === 'listening' && (
                  <>
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs text-red-400 font-medium">Listening...</span>
                  </>
                )}
                {voiceStatus === 'thinking' && (
                  <>
                    <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                    <span className="text-xs text-blue-400 font-medium">Calculating...</span>
                  </>
                )}
                {voiceStatus === 'speaking' && (
                  <>
                    <div className="flex items-center gap-0.5">
                      <div className="w-1 h-3 bg-blue-500 rounded-full animate-pulse" />
                      <div className="w-1 h-4 bg-blue-500 rounded-full animate-pulse [animation-delay:0.1s]" />
                      <div className="w-1 h-2 bg-blue-500 rounded-full animate-pulse [animation-delay:0.2s]" />
                      <div className="w-1 h-4 bg-blue-500 rounded-full animate-pulse [animation-delay:0.15s]" />
                      <div className="w-1 h-3 bg-blue-500 rounded-full animate-pulse [animation-delay:0.05s]" />
                    </div>
                    <span className="text-xs text-blue-400 font-medium">Speaking...</span>
                  </>
                )}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex gap-2 items-center"
            >
              {/* Microphone Button - Primary Voice Input */}
              {isSpeechSupported && (
                <Button
                  type="button"
                  size="icon"
                  onClick={() => {
                    onInteraction();
                    if (isListening) {
                      stopListening();
                    } else {
                      // Stop AI speech when user wants to speak
                      if (isSpeaking) {
                        stopSpeaking();
                        setCurrentSpeakingMessageId(null);
                      }
                      startListening();
                    }
                  }}
                  disabled={isTyping}
                  className={`flex-shrink-0 h-10 w-10 sm:h-11 sm:w-11 rounded-full transition-all ${
                    isListening 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse ring-2 ring-red-400/50' 
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  }`}
                  title={isListening ? "Stop listening" : "Tap to speak"}
                  aria-label={isListening ? "Stop listening" : "Tap to speak"}
                >
                  {isListening ? (
                    <MicOff className="w-5 h-5 text-white" />
                  ) : (
                    <Mic className="w-5 h-5 text-white" />
                  )}
                </Button>
              )}
              
              <Input
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  onInteraction();
                }}
                placeholder={isListening ? "Listening..." : "Where do you need to go?"}
                className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400 focus:ring-blue-500 focus:border-blue-500"
                disabled={isTyping || isListening}
                onFocus={() => {
                  onInteraction();
                  setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                  }, 300);
                }}
                onTouchStart={() => {
                  onInteraction();
                }}
              />
              <Button 
                type="submit" 
                size="icon" 
                disabled={!inputValue.trim() || isTyping || isListening} 
                className="bg-blue-600 hover:bg-blue-500 h-10 w-10 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          
          </div>
        </div>
      )}
    </>
  );
};
